import React, { useEffect, useMemo, useRef, useState } from 'react';
import { insuranceApi } from './api';
import { useProviders } from './hooks';
import {
  ContextHeader, InsuranceLogo, fmtDate,
} from './designHelpers';
import ModalShell from './ModalShell';
import isotype from '../../../../../../Assets/peicar-isotype.svg';

export default function ComposeEmailModal({ visible, onHide, qpId, persona, view, toast, onSent }) {
  const { providers } = useProviders('insurance');
  const initialProviderId = view?.aseguradora?.providerObj?.id ?? null;

  const [providerId, setProviderId] = useState(initialProviderId);
  const [showOtroInput, setShowOtroInput] = useState(false);
  const [otroCorreo, setOtroCorreo] = useState('');
  const [cc, setCc] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [showCcInput, setShowCcInput] = useState(false);
  const [asunto, setAsunto] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [sending, setSending] = useState(false);

  const fileInputRef = useRef(null);
  const bodyRef = useRef(null);

  // Reset al cerrar
  useEffect(() => {
    if (!visible) return;
    setProviderId(initialProviderId);
    setShowOtroInput(false);
    setOtroCorreo('');
    setAttachments([]);
  }, [visible, initialProviderId]);

  // Sincroniza el body en el contentEditable cuando cambia desde fuera
  // (carga de plantilla), sin pisar el cursor mientras el admin escribe.
  useEffect(() => {
    const el = bodyRef.current;
    if (el && document.activeElement !== el && el.innerText !== body) {
      el.innerText = body;
    }
  }, [body]);

  const provider = useMemo(
    () => providers.find((p) => String(p.id) === String(providerId)),
    [providers, providerId]
  );

  const aseguradora = provider
    ? { id: provider.id, nombre: provider.name, correo: provider.email, providerObj: provider }
    : view?.aseguradora || { nombre: '—', correo: '' };

  const toAddress = showOtroInput ? otroCorreo : (aseguradora.correo || '');

  // Cargar plantilla
  useEffect(() => {
    if (!visible || !qpId) return;
    setLoadingTemplate(true);
    const params = { step_type: 'add_insurance' };
    if (provider?.id) params.provider_id = provider.id;
    insuranceApi.template(qpId, params)
      .then((res) => {
        if (res?.data?.success) {
          setAsunto(res.data.template.subject || '');
          setBody(res.data.template.body || '');
          if (Array.isArray(res.data.template.default_cc) && res.data.template.default_cc.length) {
            setCc((prev) => (prev.length ? prev : res.data.template.default_cc));
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoadingTemplate(false));
  }, [visible, qpId, provider?.id]);

  const addCc = () => {
    const v = ccInput.trim();
    if (v && /\S+@\S+/.test(v) && !cc.includes(v)) { setCc([...cc, v]); setCcInput(''); }
  };

  const handleFiles = (e) => {
    const list = Array.from(e.target.files || []).map((file) => ({
      id: 'f-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
      file,
      tipo: classifyTipo(file.name),
      nombre: file.name,
      size: formatBytes(file.size),
    }));
    setAttachments((prev) => [...prev, ...list]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const canSend = !!toAddress && !!asunto && !!body && !sending && (!showOtroInput || /\S+@\S+/.test(otroCorreo));

  const handleSend = async () => {
    if (!canSend) return;
    // Lee el body más reciente del contentEditable por si no hubo blur antes del click
    const finalBody = bodyRef.current ? bodyRef.current.innerText : body;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('to', toAddress);
      cc.forEach((c) => fd.append('cc[]', c));
      fd.append('subject', asunto);
      fd.append('body', finalBody);
      if (provider?.id) fd.append('provider_id', provider.id);
      attachments.forEach((a, i) => {
        fd.append(`attachments[${i}][file]`, a.file);
        fd.append(`attachments[${i}][label]`, a.tipo);
      });

      const res = await insuranceApi.sendEmail(qpId, fd);
      if (res?.data?.success) {
        toast?.current?.show?.({ severity: 'success', summary: 'Solicitud enviada', detail: `A ${toAddress}`, life: 3500 });
        onSent?.();
        onHide?.();
      }
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'No se pudo enviar', detail: e?.response?.data?.message || e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalShell visible={visible} onClose={sending ? () => {} : onHide} stacked width={1020} height={660}>
      <button className="modal-close" onClick={onHide} disabled={sending}><i className="pi pi-times" /></button>

      {sending && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'rgba(255,255,255,0.86)',
          backdropFilter: 'blur(2px)',
          zIndex: 20,
          display: 'grid', placeItems: 'center',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner-cyan" style={{ width: 32, height: 32, borderWidth: 3, margin: '0 auto 16px' }} />
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--pei-text)' }}>Enviando solicitud…</div>
            <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4 }}>A {toAddress}</div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '440px 1fr', height: '100%', minHeight: 0 }}>
        {/* LEFT: form */}
        <div style={{ display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--pei-gray-5)', minHeight: 0 }}>
          <div style={{ padding: '26px 28px 14px' }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Paso 5 · Seguros</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.008em' }}>
              Solicitar póliza
            </div>
            <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4 }}>
              A {aseguradora.nombre}
            </div>
          </div>

          <div className="scrollable" style={{ padding: '0 28px 20px', flex: 1, minHeight: 0 }}>
            <div style={{ background: 'var(--pei-bg-soft)', borderRadius: 14, padding: 14, marginBottom: 18 }}>
              <ContextHeader cliente={view?.cliente} vehiculo={view?.vehiculo} contrato={view?.contrato} compact />
              {/* view.contrato ya viene como "No. {numeroContrato}" desde buildView */}
            </div>

            {/* Aseguradora picker */}
            <div style={{ marginBottom: 14 }}>
              <div className="pei-label">Aseguradora destino</div>
              <div style={{
                border: '1.5px solid var(--pei-cyan-bright)',
                borderRadius: 12,
                padding: '8px 10px 8px 12px',
                display: 'flex', alignItems: 'center', gap: 10,
                background: 'white',
              }}>
                <InsuranceLogo provider={aseguradora.providerObj} name={aseguradora.nombre} size={26} />
                <select
                  value={showOtroInput ? '__otro' : String(providerId ?? '')}
                  onChange={(e) => {
                    if (e.target.value === '__otro') { setShowOtroInput(true); setOtroCorreo(''); }
                    else { setShowOtroInput(false); setProviderId(e.target.value); }
                  }}
                  style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 14, fontWeight: 600, color: 'var(--pei-text)', fontFamily: 'inherit', cursor: 'pointer', outline: 'none' }}
                >
                  <option value="" disabled>Selecciona aseguradora…</option>
                  {providers.map(a => (
                    <option key={a.id} value={String(a.id)}>{a.name}</option>
                  ))}
                  <option value="__otro">+ Otro proveedor…</option>
                </select>
              </div>
            </div>

            {/* To */}
            <div style={{ marginBottom: 14 }}>
              <div className="pei-label">Para</div>
              {showOtroInput ? (
                <input
                  className="pei-input" autoFocus
                  value={otroCorreo}
                  onChange={(e) => setOtroCorreo(e.target.value)}
                  placeholder="correo@aseguradora.com"
                />
              ) : (
                <input className="pei-input" readOnly value={aseguradora.correo || ''} style={{ fontFamily: 'var(--pei-font-mono)', fontSize: 13 }} />
              )}
            </div>

            {/* CC */}
            <div style={{ marginBottom: 18 }}>
              <div className="pei-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span>CC</span>
                {!showCcInput && (
                  <button type="button" onClick={() => setShowCcInput(true)}
                    style={{ background: 'none', border: 'none', color: 'var(--pei-cyan)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'none', letterSpacing: 0 }}>
                    + Agregar
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {cc.map((email, i) => (
                  <span key={i} className="chip">
                    {email}
                    <span className="x" onClick={() => setCc(cc.filter((_, idx) => idx !== i))}>×</span>
                  </span>
                ))}
                {showCcInput && (
                  <input autoFocus className="pei-input"
                    placeholder="correo@ejemplo.com + Enter"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addCc(); }
                      if (e.key === 'Escape' && !ccInput) setShowCcInput(false);
                    }}
                    onBlur={() => { addCc(); if (!ccInput) setShowCcInput(false); }}
                    style={{ height: 34, fontSize: 13, flex: 1, minWidth: 160 }}
                  />
                )}
                {!showCcInput && cc.length === 0 && (
                  <span style={{ fontSize: 12, color: 'var(--pei-text-helper)', fontStyle: 'italic' }}>Sin destinatarios en copia</span>
                )}
              </div>
            </div>

            {/* Asunto */}
            <div style={{ marginBottom: 14 }}>
              <div className="pei-label">Asunto</div>
              <input className="pei-input" value={asunto} onChange={(e) => setAsunto(e.target.value)} disabled={loadingTemplate} />
            </div>

            {/* Hint: el mensaje se edita en la preview */}
            <div style={{
              background: 'rgba(50,184,223,0.07)',
              border: '1px dashed var(--pei-cyan-bright)',
              borderRadius: 12,
              padding: '12px 14px',
              marginBottom: 18,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <i className="pi pi-pencil" style={{ color: 'var(--pei-cyan)', fontSize: 14 }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--pei-text)', lineHeight: 1.3 }}>Edita el mensaje en la vista previa →</div>
                <div style={{ fontSize: 11, color: 'var(--pei-text-helper)', marginTop: 2 }}>Click sobre el cuerpo del correo a la derecha. Plantilla aplicada.</div>
              </div>
            </div>

            {/* Attachments */}
            <div className="pei-label" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <i className="pi pi-paperclip" style={{ fontSize: 11, color: 'var(--pei-cyan)' }} />
              Adjuntos
              <span style={{ color: 'var(--pei-text-helper)', fontWeight: 500, letterSpacing: 0, textTransform: 'none', marginLeft: 4, fontSize: 11 }}>
                · {attachments.length} {attachments.length === 1 ? 'archivo' : 'archivos'}
              </span>
            </div>
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFiles} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {attachments.map(f => (
                <AttachmentChip key={f.id} f={f} onRemove={() => setAttachments(attachments.filter(a => a.id !== f.id))} />
              ))}
              <button className="chip-add" onClick={() => fileInputRef.current?.click()} type="button">
                <i className="pi pi-paperclip" style={{ fontSize: 11 }} /> Adjuntar
              </button>
            </div>
          </div>

          <div style={{ padding: '14px 28px 22px', borderTop: '1px solid var(--pei-gray-5)', display: 'flex', gap: 10 }}>
            <button className="pei-btn pei-btn-ghost" onClick={onHide} disabled={sending} style={{ flex: '0 0 auto' }}>Cancelar</button>
            <button className="pei-btn pei-btn-primary floor-shadow" onClick={handleSend} disabled={!canSend} style={{ flex: 1 }}>
              Enviar correo <i className="pi pi-send" style={{ fontSize: 12 }} />
            </button>
          </div>
        </div>

        {/* RIGHT: preview */}
        <div style={{ background: 'var(--pei-bg-readonly)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ padding: '18px 28px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--pei-text-label)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Vista previa · lo que va a recibir {aseguradora.nombre}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontSize: 11, color: 'var(--pei-cyan)', display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                <i className="pi pi-pencil" style={{ fontSize: 11 }} /> Editable
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--pei-text-helper)' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--pei-green-bright)' }} /> En vivo
              </div>
            </div>
          </div>

          <div className="scrollable" style={{ padding: '0 28px 24px', flex: 1, minHeight: 0 }}>
            <div style={{ background: 'white', borderRadius: 14, border: '1px solid var(--pei-gray-5)', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
              <div style={{ background: 'var(--pei-gradient-tri)', height: 6 }} />

              <div style={{ padding: '22px 28px 18px', borderBottom: '1px solid var(--pei-gray-5)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <img src={isotype} alt="" style={{ height: 24 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.16em', color: 'var(--pei-blue-deeper)' }}>PEICAR</span>
                  <span style={{ fontSize: 11, color: 'var(--pei-text-helper)', marginLeft: 'auto' }}>{fmtDate(new Date().toISOString())}</span>
                </div>

                <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--pei-text)', lineHeight: 1.3, marginBottom: 10 }}>
                  {asunto || <span style={{ color: 'var(--pei-text-helper)' }}>Asunto…</span>}
                </div>

                <div style={{ fontSize: 12, color: 'var(--pei-text-helper)', lineHeight: 1.6 }}>
                  <strong style={{ color: 'var(--pei-text-label)', fontWeight: 600 }}>De:</strong> contratos@peicar.com.mx <br />
                  <strong style={{ color: 'var(--pei-text-label)', fontWeight: 600 }}>Para:</strong> {toAddress || '—'}
                  {cc.length > 0 && <><br /><strong style={{ color: 'var(--pei-text-label)', fontWeight: 600 }}>CC:</strong> {cc.join(', ')}</>}
                </div>
              </div>

              <div
                ref={bodyRef}
                contentEditable={!loadingTemplate && !sending}
                suppressContentEditableWarning
                onBlur={(e) => setBody(e.currentTarget.innerText)}
                className="editable-body"
                data-placeholder="Escribe el mensaje…"
                style={{ padding: '20px 28px 26px', fontSize: 14.5, color: 'var(--pei-text)', lineHeight: 1.65, whiteSpace: 'pre-wrap', outline: 'none', cursor: 'text', minHeight: 220 }}
              />

              {attachments.length > 0 && (
                <div style={{ padding: '0 28px 22px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--pei-text-label)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="pi pi-paperclip" style={{ fontSize: 11 }} />
                    {attachments.length} archivo{attachments.length === 1 ? '' : 's'} adjunto{attachments.length === 1 ? '' : 's'}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {attachments.map(f => (
                      <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px 8px 10px', border: '1px solid var(--pei-gray-5)', borderRadius: 10, background: 'white', minWidth: 200 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--pei-bg-soft)', display: 'grid', placeItems: 'center', color: 'var(--pei-cyan)', flexShrink: 0 }}>
                          <i className="pi pi-file-pdf" style={{ fontSize: 13, color: 'var(--pei-danger)' }} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pei-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{f.nombre}</div>
                          <div style={{ fontSize: 11, color: 'var(--pei-text-helper)' }}>{f.tipo} · {f.size}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ padding: '16px 28px', background: 'var(--pei-bg-soft)', borderTop: '1px solid var(--pei-gray-5)', fontSize: 11, color: 'var(--pei-text-helper)', textAlign: 'center', letterSpacing: '0.04em' }}>
                fácil · rápido · digital <span style={{ margin: '0 10px' }}>·</span> peicar.com.mx
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .editable-body:hover { background: rgba(50,184,223,0.06); }
        .editable-body:focus { background: rgba(50,184,223,0.10); box-shadow: inset 0 0 0 2px rgba(52,198,241,0.35); }
        .editable-body:empty::before {
          content: attr(data-placeholder);
          color: var(--pei-text-helper);
        }
      `}</style>
    </ModalShell>
  );
}

function classifyTipo(name = '') {
  const lc = name.toLowerCase();
  if (lc.includes('cotiz')) return 'Cotización';
  if (lc.includes('tarjeta') || lc.includes('circul')) return 'Tarjeta';
  if (lc.includes('ine'))     return 'INE';
  if (lc.includes('factura')) return 'Factura';
  if (lc.includes('carta'))   return 'Carta';
  if (lc.includes('cfe') || lc.includes('domicilio')) return 'Comp. domicilio';
  return 'Adjunto';
}

function formatBytes(b) {
  if (!b && b !== 0) return '';
  const k = 1024;
  if (b < k) return b + ' B';
  if (b < k * k) return Math.round(b / k) + ' KB';
  return (b / (k * k)).toFixed(1) + ' MB';
}

function AttachmentChip({ f, onRemove }) {
  const map = {
    'Cotización':      { bg: 'rgba(50,184,223,0.14)', color: 'var(--pei-cyan)' },
    'Tarjeta':         { bg: 'rgba(34,236,154,0.20)', color: '#0a7c4c' },
    'INE':             { bg: 'rgba(255,108,14,0.14)', color: 'var(--pei-orange)' },
    'Carta':           { bg: 'rgba(32,75,123,0.10)',  color: 'var(--pei-blue-deeper)' },
    'Comp. domicilio': { bg: 'rgba(50,184,223,0.14)', color: 'var(--pei-cyan)' },
    'Factura':         { bg: 'rgba(34,236,154,0.20)', color: '#0a7c4c' },
  };
  const c = map[f.tipo] || { bg: 'rgba(15,23,42,0.06)', color: 'var(--pei-text-label)' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 8px 6px 6px', background: 'white', border: '1px solid var(--pei-gray-5)', borderRadius: 999, fontSize: 12 }}>
      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 999, background: c.bg, color: c.color }}>{f.tipo}</span>
      <span style={{ color: 'var(--pei-text)', fontWeight: 500 }}>{f.nombre}</span>
      <span style={{ color: 'var(--pei-text-helper)', fontSize: 11 }}>{f.size}</span>
      <span onClick={onRemove} style={{ cursor: 'pointer', color: 'var(--pei-text-helper)', padding: '0 4px', lineHeight: 1, fontSize: 14 }}>×</span>
    </span>
  );
}
